import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Container,
  CardContent,
  Divider,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import RewardPointsChartContainer from "../../features/reward-points-tracker-card/components/RewardPointsChartContainer";
import { CLAIM_REDEMPTION } from "../../utility/constants/locale";
import StyledCard from "../../components/custom-card/StyledCard";
import { useAppSelector } from "../../store/hooks";
import CustomSpinner from "../../components/custom-spinner/CustomSpinner";
import { TIER_COLORS } from "../../utility/constants/common-constants";
import { useGetPatientRedemptionDetailsQuery } from "./GetPatientRedemptionDetailsApiSlice";
import AdminMilestonesTracker from "../../features/redemption-voucher-admin-view/components/AdminMilestonesTracker";
import { getMaxPoints } from "../../utility/functions/common-functions";

interface PatientRedemption {
  patientProfileId: number;
  patientCaseId: number;
  actualDeliveryDate: string | null;
  actualDeliveryLocationId: number | null;
  tierClassificationId: number;
  tierDisplayName: string;
  tierSequence: number;
  rangeFrom: number | null;
  rangeTo: number | null;
  nextTierCheckpoint: number;
  redemptionStatusId: number;
  redemptionId: string | null;
  statusDisplayName: string;
  redemptionCouponCode: string;
  totalEarnedRewardPoints: number | null;
  validTill: string | null;
  tierMessage?: string;
}

interface MilestoneData extends PatientRedemption {
  color: string;
}

const ClaimRedemptionContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const accessToken = useAppSelector(
    (state) => state.adminAuth.user?.accessToken!
  );

  const { data, isLoading, error } = useGetPatientRedemptionDetailsQuery({
    accessToken,
    patientProfileId: id as string,
  });

  const handleBackClick = () => {
    navigate("/admin");
  };

  const milestoneData: MilestoneData[] =
    data?.patientRedemptions.map(
      (redemption: PatientRedemption, index: number) => ({
        ...redemption,
        color: TIER_COLORS[index % TIER_COLORS.length], // Assign colors based on the sequence
      })
    ) || [];

  const getPatientName = (redemptions: any[]) => {
    return redemptions.length > 0
      ? redemptions[0].patientName
      : "Unknown Patient";
  };

  if (isLoading) return <CustomSpinner />;

  if (error) {
    return (
      <Box display="flex" justifyContent="center" mt={3}>
        <Typography variant="h6" color="error">
          Error loading milestone task.
        </Typography>
      </Box>
    );
  }
  const sanitizedInstructions = DOMPurify.sanitize(data.redemptionInstructions);

  return (
    <Container
      maxWidth={false}
      style={{ margin: "1rem auto 0" }}
      sx={{
        maxWidth: "1440px",
        padding: { xs: "0 1rem", sm: "0 3rem" },
        position: "relative",
      }}
    >
      <Box p={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={handleBackClick} aria-label="back button">
            <KeyboardArrowLeftIcon />
          </IconButton>
          <Typography variant="h2">{CLAIM_REDEMPTION}</Typography>
        </Box>
        {error ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <Typography variant="h6" color="error">
              Failed to load patient redemption details. Please try again later.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="h2" mb={2} mt={4} p={2}>
              {getPatientName(data?.patientRedemptions)}
            </Typography>
            <StyledCard
              sx={{ padding: 0, margin: 2, borderRadius: "10px" }}
              data-testid="points-tracker-milestones"
            >
              <CardContent
                sx={{
                  padding: 0,
                  ":last-child": {
                    paddingBottom: 0,
                  },
                }}
              >
                <Box display="flex" gap={2}>
                  <Box flex={1} p={2}>
                    <Box mb={3}>
                      <RewardPointsChartContainer
                        currentPoints={data?.totalEarnedRewardPoints}
                        maxPoints={getMaxPoints(data?.patientRedemptions)}
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      textAlign="center"
                      px={3}
                      mb={8}
                    >
                      {data?.rewardMilestoneMessage}
                    </Typography>
                    <Divider
                      sx={{
                        my: 2,
                        borderStyle: "dashed",
                        borderBottomWidth: "2px",
                        borderColor: "#BCA685",
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      component="div"
                      px={3}
                      fontWeight={500}
                      mb={2}
                    >
                      Redemption steps to follow
                    </Typography>
                    <Typography
                      variant="body2"
                      px={3}
                      lineHeight="30px"
                      dangerouslySetInnerHTML={{
                        __html: sanitizedInstructions,
                      }}
                    />
                  </Box>
                  <Box flex={1}>
                    <AdminMilestonesTracker milestoneData={milestoneData} />
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </>
        )}
      </Box>
    </Container>
  );
};

export default ClaimRedemptionContainer;
